import React, {useEffect, useCallback, useState, useMemo} from 'react'
import TagManager from 'react-gtm-module'

import {Auth0Provider, useAuth0} from '@auth0/auth0-react'
import {setLogUser, addLogMetadata, log} from '@vanguard/logger'
import {api} from '../services/api'
import {
  AUTH_SESSION_STORAGE_KEY,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  AUTH_CLIENTID_SESSION_STORAGE_KEY,
} from '../constants'
import {
  Client,
  Person,
  FeatureSettings,
  Feature,
  AuthState,
  UserPermissions,
} from '../types'

import {useSecureStoredReducer} from '../hooks/useSecureStoredReducer'
import {
  TokenOperationResponse,
  TokenService,
} from './auth/services/TokenService'
import {initializeSession} from './auth/services/SessionService'

type SessionResult = 'success' | 'login_required' | 'error'

type Action =
  | {
      type: 'LOGIN_SUCCESS'
      payload: {
        client: Client
        person: Person
        features: Feature[]
        featureSettings: FeatureSettings
        userPermissions: UserPermissions
      }
    }
  | {
      type: 'LOGIN_FAILURE'
    }
  | {
      type: 'SET_AUTH_CLIENT_ID'
      payload: {clientID: string}
    }
  | {
      type: 'LOGOUT_SUCCESS'
    }
  | {
      type: 'INITIATE_LOGIN_WITH_IDP'
    }
  | {
      type: 'INITIATE_LOGIN'
    }
  | {
      type: 'INITIATE_LOGOUT'
    }
  | {
      type: 'IDP_AUTHENTICATED'
    }
  | {
      type: 'INITIATE_EXCHANGE_TOKEN'
    }
  | {
      type: 'EXCHANGE_TOKEN_FAILURE'
    }
  | {
      type: 'REFRESH_SESSION_SUCCESS'
    }

// This removes the null type from the AuthState
type RequiredAuthState = {
  [P in keyof AuthState]: NonNullable<AuthState[P]>
}

interface AuthStore extends AuthState {
  logout(returnToPath?: string): Promise<void>
  handleRefreshSession: () => Promise<SessionResult>
  loginWithIdp: () => Promise<void>
  setUseIdpAuthentication(): void
  setAuthClientID: (clientID: string) => void
  setError: (error: Error) => void
  getAccessToken: () => Promise<string>
  error: Error | undefined
  loading: boolean
}

const AuthContext = React.createContext<AuthStore | undefined>(undefined)

export const useAuth = (): AuthStore => {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider')
  }

  return context
}

// Expects the auth state properties are set values (not null), else it's an error
// This is great for using for api calls that you know the person and client id
// are there so you can avoid having null checks everywhere
export const useRequiredAuth = (): RequiredAuthState => {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider')
  }

  const {
    client,
    person,
    releaseFlags,
    userPermissions,
    authenticationStatus,
    useIdpAuthentication,
  } = context

  if (!client || !person) {
    // TODO: Perhaps check required auth items and logout with reason instead of throw error

    throw new Error(
      'RequiredState values cannot be null or undefined in useRequiredAuth',
    )
  }

  return {
    client,
    person,
    releaseFlags: releaseFlags || [],
    userPermissions: userPermissions || [],
    authenticationStatus,
    useIdpAuthentication,
  }
}

export const authReducer: React.Reducer<AuthState, Action> = (
  state,
  action,
) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS': {
      const releaseFlags =
        (action.payload.featureSettings.clientFeatures &&
          action.payload.featureSettings.clientFeatures.map((cf) => {
            const feature = action.payload.features.find(
              (feat) => feat.ID === cf.featureID,
            )

            return {
              ID: cf.featureID,
              name: (feature && feature.name) || 'unknown',
              displayName: (feature && feature.displayName) || 'unknown',
              description: (feature && feature.description) || 'unknown',
            }
          })) ||
        []

      return {
        ...state,
        person: action.payload.person,
        client: action.payload.client,
        releaseFlags,
        userPermissions: action.payload.userPermissions.permissions,
        authenticationStatus: 'authenticated',
      }
    }
    case 'LOGIN_FAILURE': {
      return {
        ...state,
        authenticationStatus: 'unauthenticated',
        authClientID: undefined,
      }
    }
    case 'SET_AUTH_CLIENT_ID': {
      return {
        ...state,
        authClientID: action.payload.clientID,
      }
    }
    case 'LOGOUT_SUCCESS': {
      return {
        ...state,
        person: null,
        client: null,
        releaseFlags: null,
        userPermissions: null,
        authenticationStatus: 'unauthenticated',
        useIdpAuthentication: false,
      }
    }
    case 'INITIATE_LOGIN_WITH_IDP': {
      return {
        ...state,
        useIdpAuthentication: true,
      }
    }

    case 'INITIATE_LOGOUT': {
      return {
        ...state,
        authenticationStatus: 'pending',
      }
    }

    case 'INITIATE_LOGIN': {
      return {
        ...state,
        authenticationStatus: 'pending',
      }
    }

    case 'IDP_AUTHENTICATED': {
      return {
        ...state,
        authenticationStatus: 'idp_authenticated',
        useIdpAuthentication: true,
      }
    }

    case 'INITIATE_EXCHANGE_TOKEN': {
      return {
        ...state,
        authenticationStatus: 'pending',
      }
    }

    case 'EXCHANGE_TOKEN_FAILURE': {
      return {
        ...state,
        authenticationStatus: 'unauthenticated',
      }
    }

    case 'REFRESH_SESSION_SUCCESS': {
      return {
        ...state,
        authenticationStatus: 'authenticated',
      }
    }

    default:
      throw new Error('Unhandled action')
  }
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({children}) => {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        scope: 'openid profile email offline_access',
        audience: 'https://api.campanionapp.com',
      }}
      cacheLocation='localstorage'
      useRefreshTokens>
      <AuthContextProvider>{children}</AuthContextProvider>
    </Auth0Provider>
  )
}

const AuthContextProvider: React.FC<React.PropsWithChildren> = ({children}) => {
  // Get the persisted auth client ID from localStorage if it exists
  // Not relying on the one in the reducer because I want more UX flexibility with local storage for it
  // But I also didn't want to separate it from the AuthState and the reducer
  const persistedClientId = localStorage.getItem(
    AUTH_CLIENTID_SESSION_STORAGE_KEY,
  )

  const [state, dispatch] = useSecureStoredReducer(
    AUTH_SESSION_STORAGE_KEY,
    authReducer,
    {
      client: null,
      person: null,
      releaseFlags: null,
      userPermissions: null,
      authenticationStatus: 'unauthenticated',
      useIdpAuthentication: false,
      // Use the persisted client ID if available
      authClientID: persistedClientId || undefined,
    },
  )
  const {
    getAccessTokenSilently,
    logout: auth0Logout,
    isAuthenticated: isAuthenticatedWithAuth0,
    loginWithRedirect,
    isLoading: isAuth0Loading,
    error: auth0Error,
  } = useAuth0()

  const setAuthClientID = useCallback(
    (clientID: string): void => {
      // Always update localStorage to ensure it's the source of truth for the selected client
      if (clientID && clientID !== '0') {
        localStorage.setItem(AUTH_CLIENTID_SESSION_STORAGE_KEY, clientID)
      } else {
        localStorage.removeItem(AUTH_CLIENTID_SESSION_STORAGE_KEY)
      }
      // The reducer will handle updating sessionStorage via useSecureStoredReducer
      dispatch({type: 'SET_AUTH_CLIENT_ID', payload: {clientID}})
    },
    [dispatch],
  )

  const [error, setError] = useState<Error | undefined>()

  // Create TokenService instance
  const tokenService = useMemo(
    () => new TokenService(getAccessTokenSilently, loginWithRedirect),
    [getAccessTokenSilently, loginWithRedirect],
  )

  // When user logs in successfully or the page is refreshed, this sets additional logger data for error reporting
  const authStatePersonID = (state.person && state.person.ID) || 'unset'
  const authStateClientID = (state.client && state.client.ID) || 'unset'
  const authStateCampName = (state.client && state.client.name) || 'unset'

  setLogUser(authStatePersonID)
  addLogMetadata('client', {ID: authStateClientID, Name: authStateCampName})

  const logout = async (returnToPath?: string): Promise<void> => {
    dispatch({
      type: 'INITIATE_LOGOUT',
    })

    if (isAuthenticatedWithAuth0) {
      try {
        await auth0Logout({
          logoutParams: {
            returnTo: `${window.location.origin}/${returnToPath || 'logout'}`,
          },
        })
      } catch (err) {
        if (err instanceof Error) {
          log.error(err)
        } else {
          log.error(new Error(JSON.stringify(err)))
        }
      }
    }

    if (state.authenticationStatus === 'authenticated') {
      sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
      try {
        const response = await api.auth.logout()

        if (response.result !== 'success') {
          throw new Error(
            response.error?.message ||
              'unexpected error occurred while logging out',
          )
        }
      } catch (err) {
        if (err instanceof Error) {
          log.error(err)
        } else {
          log.error(new Error(JSON.stringify(err)))
        }
        return Promise.reject()
      }
    }

    dispatch({
      type: 'LOGOUT_SUCCESS',
    })

    return Promise.resolve()
  }

  const loginWithIdp = async (): Promise<void> => {
    await loginWithRedirect()
  }

  const setUseIdpAuthentication = (): void => {
    dispatch({
      type: 'INITIATE_LOGIN_WITH_IDP',
    })
  }

  const handleTokenExchange =
    useCallback(async (): Promise<TokenOperationResponse> => {
      // Dispatch pending state
      dispatch({
        type: 'INITIATE_EXCHANGE_TOKEN',
      })

      const tokenResult = await tokenService.validateAndExchangeToken(
        state.authClientID,
        state.useIdpAuthentication,
      )

      if (tokenResult.status === 'error') {
        log.error(
          new Error(`Token exchange failed: ${tokenResult.error?.message}`),
        )

        dispatch({
          type: 'EXCHANGE_TOKEN_FAILURE',
        })
        setError(tokenResult.error)
      }

      if (!tokenResult.data) {
        const unexpectedError = new Error(
          'Token exchange succeeded but no data returned',
        )

        log.error(unexpectedError)

        dispatch({
          type: 'EXCHANGE_TOKEN_FAILURE',
        })
        setError(unexpectedError)
      }

      return tokenResult
    }, [dispatch, state.authClientID, state.useIdpAuthentication, tokenService])

  // Helper method to handle refresh session with comprehensive logging and business logic
  const handleRefreshSession: AuthStore['handleRefreshSession'] =
    useCallback(async (): Promise<SessionResult> => {
      const tokenResult = await handleTokenExchange()
      if (tokenResult.data) {
        dispatch({
          type: 'REFRESH_SESSION_SUCCESS',
        })
        setError(undefined)
      }

      return tokenResult.status
    }, [dispatch, handleTokenExchange])

  // after user is logged in with IDP and a client is selected
  // exchange the user's access token for legacy token
  // then fetch user's data based on the legacy token
  const initializeUserSession = useCallback(async () => {
    try {
      const tokenResult = await handleTokenExchange()

      // Handle token exchange failure
      if (tokenResult.status !== 'success' || !tokenResult.data) {
        const errorMessage =
          tokenResult.status === 'login_required'
            ? 'Authentication required - user needs to log in again'
            : `Token exchange failed: ${
                tokenResult.error?.message || 'Unknown error'
              }`

        const tokenError = new Error(errorMessage)

        dispatch({
          type: 'LOGIN_FAILURE',
        })
        setError(tokenError)
        setAuthClientID('') // ensuring the local storage variable is cleared
        return
      }

      // Initialize session with new token data
      const sessionResult = await initializeSession(tokenResult.data)

      if (sessionResult.status === 'success' && sessionResult.data) {
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: sessionResult.data,
        })
        setError(undefined)
        return
      }

      // Handle session initialization failure
      const sessionError = new Error(
        `Session initialization failed: ${
          sessionResult.error?.message || 'Failed to load user data'
        }`,
      )

      dispatch({
        type: 'LOGIN_FAILURE',
      })
      setError(sessionError)
      setAuthClientID('') // ensuring the local storage variable is cleared
    } catch (unexpectedError) {
      // Handle any unexpected errors that might occur
      const sessionInitError =
        unexpectedError instanceof Error
          ? unexpectedError
          : new Error(
              `Unexpected error during user session initialization: ${JSON.stringify(
                unexpectedError,
              )}`,
            )

      dispatch({
        type: 'LOGIN_FAILURE',
      })
      setError(sessionInitError)
      setAuthClientID('') // ensuring the local storage variable is cleared
    }
  }, [dispatch, handleTokenExchange, setAuthClientID])

  // Send needed data to Google Tag Manager. Used to trigger marketing tags or provide context to them.
  // This is not apart of login success flow because someone could refresh and then we'd lose the context.
  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        personID: state.person?.ID,
        firstName: state.person?.name.first,
        lastName: state.person?.name.last,
        email: state.person?.email,
        clientID: state.client?.ID,
        campName: state.client?.name,
        salesforceAccountID: state.client?.salesforceAccountID,
      },
    })
  }, [state.client, state.person])

  // When user has been authenticated wiith Auth0, update state
  useEffect(() => {
    if (isAuthenticatedWithAuth0) {
      dispatch({
        type: 'IDP_AUTHENTICATED',
      })
    }
  }, [dispatch, isAuthenticatedWithAuth0])

  // Initialize session on auth state changes, but wait for Auth0 to finish loading
  useEffect(() => {
    if (state.useIdpAuthentication && !isAuth0Loading && state.authClientID) {
      initializeUserSession()
    }
  }, [
    state.useIdpAuthentication,
    state.authClientID,
    initializeUserSession,
    isAuth0Loading,
  ])

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value: AuthStore = {
    logout,
    handleRefreshSession,
    loginWithIdp,
    setUseIdpAuthentication, // TODO: might be unneeded
    setAuthClientID,
    setError,
    error: error || auth0Error,
    loading: isAuth0Loading,
    getAccessToken: getAccessTokenSilently,
    ...state,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
